import React, { useState, useEffect } from 'react';
import type { WebhookStock } from '../../services/trading';

interface SellPositionDialogProps {
  isOpen: boolean;
  stock: WebhookStock | null;
  onClose: () => void;
  onConfirm: (quantity: number, price?: number) => void;
  isLoading: boolean;
  error?: string | null;
}

export function SellPositionDialog({
  isOpen,
  stock,
  onClose,
  onConfirm,
  isLoading,
  error,
}: SellPositionDialogProps) {
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize quantity when stock changes
  useEffect(() => {
    if (stock) {
      setQuantity(stock.currentPosition.toString());
      setPrice(''); // Clear price for new stock
      setValidationError(null);
    }
  }, [stock]);

  if (!isOpen || !stock) return null;

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuantity(value);

    // Clear validation error when user starts typing
    setValidationError(null);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrice(value);

    // Clear validation error when user starts typing
    setValidationError(null);
  };

  const handleConfirm = () => {
    const qty = parseFloat(quantity);
    const sellPrice = price.trim() ? parseFloat(price) : undefined;

    // Validation
    if (isNaN(qty) || qty <= 0) {
      setValidationError('Quantity must be greater than 0');
      return;
    }

    if (qty > stock.currentPosition) {
      setValidationError(`Cannot sell more than ${stock.currentPosition} shares`);
      return;
    }

    // Validate price if provided
    if (sellPrice !== undefined && (isNaN(sellPrice) || sellPrice <= 0)) {
      setValidationError('Price must be greater than 0');
      return;
    }

    onConfirm(qty, sellPrice);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        {/* Dialog Header */}
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Confirm Sell Order
        </h3>

        {/* Order Summary */}
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Symbol:</span>
              <span className="font-medium text-gray-900 dark:text-white">{stock.ticker}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Action:</span>
              <span className="font-medium text-red-600 dark:text-red-400">SELL</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Shares:</span>
              <div className="flex flex-col items-end">
                <input
                  type="number"
                  value={quantity}
                  onChange={handleQuantityChange}
                  disabled={isLoading}
                  step="0.01"
                  min="0"
                  max={stock.currentPosition}
                  className="w-32 px-3 py-1 text-right border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Max: {stock.currentPosition}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Price for Records:</span>
              <div className="flex flex-col items-end">
                <input
                  type="number"
                  value={price}
                  onChange={handlePriceChange}
                  disabled={isLoading}
                  step="0.01"
                  min="0"
                  placeholder="Execution price"
                  className="w-32 px-3 py-1 text-right border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  For P&L tracking only
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Account:</span>
              <span className="font-medium text-gray-900 dark:text-white">{stock.accountName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Brokerage:</span>
              <span className="font-medium text-gray-900 dark:text-white">{stock.brokerageName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Order Type:</span>
              <span className="font-medium text-gray-900 dark:text-white">Market Order</span>
            </div>
          </div>
        </div>

        {/* Warning Message */}
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex">
            <svg
              className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> Order executes at market price. The price field above is for recording the actual execution price to ensure accurate P&L calculations.
            </div>
          </div>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm text-red-800 dark:text-red-200">
                <strong>Error:</strong> {validationError}
              </div>
            </div>
          </div>
        )}

        {/* API Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm text-red-800 dark:text-red-200">
                <strong>Error:</strong> {error}
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                Executing sell order...
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isLoading ? 'Selling...' : 'Confirm Sell'}
          </button>
        </div>
      </div>
    </div>
  );
}

